import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Grid, Paper, Divider, Tooltip, CircularProgress, Alert, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, MenuItem, Select, InputLabel } from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CompanyDetails = () => {
  const [accounts, setAccounts] = useState([{ accountNumber: "", purpose: "", bankname: "", ifsc: "" }]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [existingCompanyId, setExistingCompanyId] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const [companyData, setCompanyData] = useState({
    companyname: "",
    country: "India",
    currency_code: "INR",
    currency_symbol: "₹",
    timezone: "Asia/Kolkata",
    emailDomain: "",
    emailGenMode: "manual",
    contactemail: "",
    contactNumber: "",
    companyLogo: null,
    hq: "",
    address: "",
    branchLocation: "",
    tan: "",
    pfTrustName: "",
    pfRegno: "",
    pfAddress: "",
    CIN: "",
    industrySector: "",
    tenant_id: "",
    PAN: "",
    GST: "",
    SGST: "",
    CGST: "",
    ESI: "",
    nationalIdLabel: "",
    bankCodeLabel: "",
    taxLabel: ""
  });

  const [errors, setErrors] = useState({});

  // Fetch user and company data on component mount
  useEffect(() => {
    fetchUserAndCompanyData();
  }, []);

  const fetchUserAndCompanyData = async () => {
    setLoading(true);
    setFetchError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFetchError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      // Decode token to get user ID
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.id;

      // Fetch user details from new user endpoint
      const userResponse = await axios.get(`${API_URL}/api/cmp/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = userResponse.data.data;
      const companyId = userData.company_id;

      if (companyId) {
        // User has an existing company, fetch company details
        await fetchCompanyDetails(companyId, token);
      } else {
        // New user, show empty form
        setLoading(false);
      }

    } catch (error) {
      setFetchError("Failed to load user data. Please try again.");
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async (companyId, token) => {
    try {
      const companyResponse = await axios.get(`${API_URL}/api/cmp/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const company = companyResponse.data.data;
      // Populate form with existing data
      setCompanyData({
        companyname: company.companyname || "",
        country: company.country || "India",
        currency_code: company.currency_code || "INR",
        currency_symbol: company.currency_symbol || "₹",
        timezone: company.timezone || "Asia/Kolkata",
        emailDomain: company.emailDomain || "",
        emailGenMode: company.emailGenMode || "manual",
        contactemail: company.contactemail || "",
        contactNumber: company.contactNumber || "",
        companyLogo: company.companyLogo, // This will be the filename
        hq: company.hq || "",
        address: company.address || "",
        branchLocation: company.branchLocation || "",
        tan: company.tan || "",
        pfTrustName: company.pfTrustName || "",
        pfRegno: company.pfRegno || "",
        pfAddress: company.pfAddress || "",
        CIN: company.CIN || "",
        industrySector: company.industrySector || "",
        tenant_id: company.tenant_id || "",
        PAN: company.PAN || "",
        GST: company.GST || "",
        SGST: company.SGST || "",
        CGST: company.CGST || "",
        ESI: company.ESI || "",
        nationalIdLabel: company.nationalIdLabel || "Company Registration No",
        bankCodeLabel: company.bankCodeLabel || "IFSC / Bank Code",
        taxLabel: company.taxLabel || "Tax ID"
      });

      // Populate accounts if they exist
      if (company.accounts && company.accounts.length > 0) {
        setAccounts(company.accounts);
      }

      setExistingCompanyId(companyId);
      setIsEditing(true);
      
    } catch (error) {
      setFetchError("Failed to load company details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (e, index) => {
    const { name, value } = e.target;
    const newAccounts = [...accounts];
    newAccounts[index][name] = value;
    setAccounts(newAccounts);
  };
  
  const addAccount = () => {
    setAccounts([...accounts, { accountNumber: "", purpose: "", bankname: "", ifsc: "" }]);
  };
  
  const removeAccount = (index) => {
    const newAccounts = accounts.filter((_, i) => i !== index);
    setAccounts(newAccounts);
  };

  const validate = () => {
    const newErrors = {};
    const isIndia = !companyData.country || companyData.country.toLowerCase() === "india";

    if (!companyData.companyname) newErrors.companyname = "Company name is required.";
    if (!companyData.emailDomain) newErrors.emailDomain = "Email domain is required.";
    
    if (!companyData.CIN) {
      newErrors.CIN = `${companyData.nationalIdLabel || "Company Registration No"} is required.`;
    }
    
    if (isIndia) {
      if (!companyData.PAN) {
        newErrors.PAN = "PAN is required.";
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(companyData.PAN)) {
        newErrors.PAN = "Invalid PAN format. (e.g., ABCDE1234F)";
      }   
      
      if (!companyData.GST) {
        newErrors.GST = "GST is required.";
      } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(companyData.GST)) {
        newErrors.GST = "Invalid GST format. (e.g., 27ABCDE1234F1Z5)";
      }   
      
      if (!companyData.SGST) {
        newErrors.SGST = "SGST is required.";
      } else if (!/^\d+(\.\d{1,2})?%?$/.test(companyData.SGST)) {
        newErrors.SGST = "Invalid SGST. Use a percentage (e.g., 9%) or numeric value.";
      }    
      
      if (!companyData.CGST) {
        newErrors.CGST = "CGST is required.";
      } else if (!/^\d+(\.\d{1,2})?%?$/.test(companyData.CGST)) {
        newErrors.CGST = "Invalid CGST. Use a percentage (e.g., 9%) or numeric value.";
      }  
      
      if (!companyData.tan) {
        newErrors.tan = "TAN is required (eg:ABCD09876H).";
      } else if (!/^[A-Za-z]{4}\d{5}[A-Za-z]{1}$/.test(companyData.tan)) {
        newErrors.tan = "TAN should be alphanumeric with 10 characters.";
      }
    } else {
      // Non-India: GST field acts as general Tax / VAT registration number
      if (!companyData.GST) {
        newErrors.GST = `${companyData.taxLabel} Registration Number is required.`;
      }
    }
    
    if (!companyData.contactemail) {
      newErrors.contactemail = "Email is required.";
    } else if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(companyData.contactemail)) {
      newErrors.contactemail = "Enter a valid email.";
    }

    if (!companyData.contactNumber) {
      newErrors.contactNumber = "Contact number is required.";
    } else if (!/^\d{7,15}$/.test(companyData.contactNumber.replace(/[\s-]/g, ''))) {
      newErrors.contactNumber = "Please enter a valid contact number (7-15 digits).";
    }

    if (!companyData.address) newErrors.address = "Address is required.";
    if (!companyData.branchLocation) newErrors.branchLocation = "Branch Location is required.";
    if (!companyData.hq) newErrors.hq = "Head quarters is required.";

    accounts.forEach((account, index) => {
      if (!account.accountNumber) {
        newErrors[`accountNumber_${index}`] = `Account number is required for account ${index + 1}`;
      } else if (!/^[A-Za-z0-9\s-]{6,34}$/.test(account.accountNumber)) {
        newErrors[`accountNumber_${index}`] = `Invalid account number for account ${index + 1}`;
      }

      if (!account.purpose) {
        newErrors[`purpose_${index}`] = `Purpose is required for account ${index + 1}`;
      }

      if (!account.ifsc) {
        newErrors[`ifsc_${index}`] = `${companyData.bankCodeLabel} is required for account ${index + 1}.`;
      } else if (isIndia && !/^[A-Za-z]{4}\d{7}$/.test(account.ifsc)) {
        newErrors[`ifsc_${index}`] = `IFSC code must be 4 letters followed by 7 digits for account ${index + 1}`;
      } else if (!isIndia && account.ifsc.length < 3) {
        newErrors[`ifsc_${index}`] = `Please enter a valid ${companyData.bankCodeLabel} for account ${index + 1}`;
      }
      
      if (!account.bankname) {
        newErrors[`bankname_${index}`] = `Bank name is required for account ${index + 1}`;
      }
    });

    // Only validate logo for new companies (if no existing logo)
    if (!isEditing && !companyData.companyLogo) {
      newErrors.companyLogo = "Company Logo is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setCompanyData({ ...companyData, companyLogo: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setSubmitting(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert("No authentication token found. Please log in.");
          setSubmitting(false);
          return;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.id;
        const userRole = payload.role;

        const formData = new FormData();
  
        Object.keys(companyData).forEach((key) => {
          if (key === "companyLogo") {
            if (companyData.companyLogo instanceof File) {
              // New file selected
              formData.append("companyLogo", companyData.companyLogo);
            } else if (isEditing && companyData.companyLogo) {
              // Existing logo filename, append as string
              formData.append("existingLogo", companyData.companyLogo);
            }
          } else {
            formData.append(key, companyData[key]);
          }
        });
  
        formData.append("accounts", JSON.stringify(accounts));
  
        let response;
        if (isEditing && existingCompanyId) {
          // Update existing company
          const url = `${API_URL}/api/cmp/${existingCompanyId}?id=${userId}&role=${userRole}`;
          response = await axios.put(url, formData, {
            headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}`, }
          });
        } else {
          // Create new company
          const url = `${API_URL}/api/create?id=${userId}&role=${userRole}`;
          response = await axios.post(url, formData, {
            headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}`, }
          });
        }
  
        alert(isEditing ? "Company updated successfully!" : "Company created successfully!");
  
        // If it's a new company, set editing state for next time
        if (!isEditing && response.data.company) {
          setExistingCompanyId(response.data.company.registration_no);
          setIsEditing(true);
        }
  
      } catch (error) {
      
      // ✅ FIX: Display the actual error message from backend
      let errorMessage = "Failed to save company. Please try again.";
      
      if (error.response) {
        // The request was made and the server responded with a status code

        
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        // Add more specific messages based on status code
        if (error.response.status === 409) {
          errorMessage = "❌ " + (error.response.data.message || "A company with this CIN already exists. Please use a unique CIN.");
        } else if (error.response.status === 403) {
          errorMessage = "⛔ " + (error.response.data.message || "You don't have permission to perform this action.");
        } else if (error.response.status === 404) {
          errorMessage = "🔍 " + (error.response.data.message || "Resource not found.");
        } else if (error.response.status === 400) {
          errorMessage = "⚠️ " + (error.response.data.message || "Invalid request. Please check your input.");
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "No response from server. Please check your internet connection.";
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage = error.message || "An unexpected error occurred.";
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }
};

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ maxWidth: 1500, margin: 'auto', padding: 3 }}>
        <Alert severity="error">{fetchError}</Alert>
        <Button 
          variant="contained" 
          onClick={fetchUserAndCompanyData} 
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1500, margin: 'auto', padding: 3 }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        {isEditing ? "Edit Company Details" : "Add Company Details"}
      </Typography>

      {isEditing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are editing an existing company. The {companyData.nationalIdLabel || "CIN/Registration No"} field is disabled as it cannot be changed.
        </Alert>
      )}
      
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Company Details
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Tooltip title="Enter Company Name" placement="top" arrow>
              <TextField
                label="Company Name"
                name="companyname"
                value={companyData.companyname}
                onChange={handleChange}
                error={!!errors.companyname}
                helperText={errors.companyname}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6}>
            <Tooltip title="Registered Country" placement="top" arrow>
              <TextField
                label="Country"
                name="country"
                value={companyData.country || "India"}
                fullWidth
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6}>
            <Tooltip title="Company Base Currency" placement="top" arrow>
              <TextField
                label="Base Currency"
                name="currency_code"
                value={`${companyData.currency_code || 'INR'} (${companyData.currency_symbol || '₹'})`}
                fullWidth
                disabled
                InputProps={{
                  readOnly: true,
                }}
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Enter Company Email Domain eg:abctech.com" placement="top" arrow>
              <TextField
                label="Company Email Domain"
                name="emailDomain"
                value={companyData.emailDomain}
                onChange={handleChange}
                error={!!errors.emailDomain}
                helperText={errors.emailDomain}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="email-gen-mode-label">Employee Email Generation Mode</InputLabel>
              <Select
                labelId="email-gen-mode-label"
                id="emailGenMode"
                name="emailGenMode"
                value={companyData.emailGenMode || "manual"}
                label="Employee Email Generation Mode"
                onChange={handleChange}
              >
                <MenuItem value="automated">Automated (Auto-generate via company domain)</MenuItem>
                <MenuItem value="manual">Manual (Admin manually types full email)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title={`Enter ${companyData.nationalIdLabel || 'Company Registration No'}`} placement="top" arrow>
              <TextField
                label={companyData.nationalIdLabel || 'Company Registration No'}
                placeholder={`Enter ${companyData.nationalIdLabel || 'Company Registration No'}`}
                name="CIN"
                value={companyData.CIN}
                onChange={handleChange}
                error={!!errors.CIN}
                helperText={errors.CIN}
                fullWidth
                required
                disabled={isEditing} // Disable CIN when editing
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Industry Sector" placement="top" arrow>
              <TextField
                label="Industry Sector"
                name="industrySector"
                value={companyData.industrySector || ""}
                onChange={handleChange}
                fullWidth
                helperText="Primary industry or business sector"
              />
            </Tooltip>
          </Grid>

         {/* <Grid item xs={6}>
            <Tooltip title="BCC Tenant Identifier (Read-Only)" placement="top" arrow>
              <TextField
                label="Tenant ID"
                name="tenant_id"
                value={companyData.tenant_id || ""}
                fullWidth
                disabled
                InputProps={{
                  readOnly: true,
                }}
                helperText="Assigned by BCC (Read-only)"
              />
            </Tooltip>
          </Grid>
          */}
          
          <Grid item xs={6}>
            <Tooltip title="Upload Company Logo" placement="top" arrow>
              <Box>
                <Typography sx={{ color: errors.companyLogo ? 'error.main' : 'inherit', fontWeight: 500 }}>
                  Company Logo {!isEditing && <span style={{ color: '#888', fontWeight: 300 }}>*</span>}
                </Typography>
                {isEditing && companyData.companyLogo && typeof companyData.companyLogo === 'string' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 1 }}>
                    <img 
                      src={companyData.companyLogo.startsWith('http') ? companyData.companyLogo : `${API_URL}/${companyData.companyLogo}`} 
                      alt="Company Logo" 
                      style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', border: '1px solid #ccc' }} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>
                      Current logo: {companyData.companyLogo.split('/').pop().split('-').slice(1).join('-') || companyData.companyLogo.split('/').pop()}
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{
                    border: errors.companyLogo ? '1px solid #d32f2f' : undefined,
                    borderRadius: 4,
                    padding: 8,
                    marginTop: 4,
                    width: '100%'
                  }}
                  onChange={handleFileChange}
                />
                {errors.companyLogo && (
                  <Typography variant="caption" color="error">
                    {errors.companyLogo}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Grid>

          <Grid item xs={6}>
            <Tooltip title="Enter Company Email" placement="top" arrow>
              <TextField
                label="Contact Email"
                name="contactemail"
                value={companyData.contactemail}
                onChange={handleChange}
                error={!!errors.contactemail}
                helperText={errors.contactemail}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Enter Company Number" placement="top" arrow>
              <TextField
                label="Contact Number"
                name="contactNumber"
                value={companyData.contactNumber}
                onChange={handleChange}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Enter Company Head Quarters" placement="top" arrow>
              <TextField
                label="Head Quarters"
                name="hq"
                value={companyData.hq}
                onChange={handleChange}
                error={!!errors.hq}
                helperText={errors.hq}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={6}>
            <Tooltip title="Enter Branch Location" placement="top" arrow>
              <TextField
                label="Branch Location"
                name="branchLocation"
                value={companyData.branchLocation}
                onChange={handleChange}
                error={!!errors.branchLocation}
                helperText={errors.branchLocation}
                fullWidth
                required
              />
            </Tooltip>
          </Grid>
          
          <Grid item xs={12}>
            <Tooltip title="Enter Company Address" placement="top" arrow>
              <TextField
                label="Address"
                name="address"
                value={companyData.address}
                onChange={handleChange}
                error={!!errors.address}
                helperText={errors.address}
                fullWidth
                required
                multiline
                rows={2}
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Details
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        {accounts.map((acc, index) => (
          <Grid container spacing={2} key={index} alignItems="center">
            <Grid item xs={3}>
              <Tooltip title="Enter Company Account Number" placement="top" arrow>
                <TextField
                  margin="normal"
                  label="Account Number"
                  name="accountNumber"
                  value={acc.accountNumber}
                  error={!!errors[`accountNumber_${index}`]}
                  helperText={errors[`accountNumber_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>

            <Grid item xs={3}>
              <Tooltip title="Enter Purpose of the Account" placement="top" arrow>
                <TextField
                  margin="normal"
                  label="Purpose"
                  name="purpose"
                  value={acc.purpose}
                  error={!!errors[`purpose_${index}`]}
                  helperText={errors[`purpose_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>
            
            <Grid item xs={3}>
              <Tooltip title="Enter Bank Name" placement="top" arrow>
                <TextField
                  label="Bank Name"
                  name="bankname"
                  value={acc.bankname}
                  error={!!errors[`bankname_${index}`]}
                  helperText={errors[`bankname_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>

            <Grid item xs={3}>
              <Tooltip title={`Enter ${companyData.bankCodeLabel || 'Bank Code'}`} placement="top" arrow>
                <TextField
                  label={companyData.bankCodeLabel || 'Bank Code'}
                  placeholder={`Enter ${companyData.bankCodeLabel || 'Bank Code'}`}
                  name="ifsc"
                  value={acc.ifsc}
                  error={!!errors[`ifsc_${index}`]}
                  helperText={errors[`ifsc_${index}`]}
                  onChange={(e) => handleAccountChange(e, index)}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>

            {index > 0 && (
              <Grid item xs={0.4}>
                <CloseIcon
                  sx={{ cursor: "pointer", color: "black", marginLeft: 1, marginTop: 3 }}
                  onClick={() => removeAccount(index)}
                />
              </Grid>
            )}
          </Grid>
        ))}

        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
          <Button variant="outlined" onClick={addAccount}>
            Add Another Account
          </Button>
        </Box>
      </Paper>

      {/* Tax & Regulatory Details */}
      <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tax & Regulatory Details
        </Typography>
        <Divider sx={{ marginBottom: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Tooltip title={`Enter ${companyData.taxLabel || 'Tax ID'}`} placement="top" arrow>
              <TextField
                label={(companyData.taxLabel || 'Tax ID').includes("IVA") || (companyData.taxLabel || 'Tax ID').includes("Number") 
                  ? (companyData.taxLabel || 'Tax ID') 
                  : `${companyData.taxLabel || 'Tax ID'} Registration Number`}
                name="GST"
                value={companyData.GST}
                onChange={handleChange}
                fullWidth
                error={!!errors.GST}
                helperText={errors.GST}
                required
              />
            </Tooltip>
          </Grid>

          {(!companyData.country || companyData.country.toLowerCase() === "india") && (
            <>
              <Grid item xs={6}>
                <Tooltip title="Enter PAN Number(eg: ABCDE0978H)" placement="top" arrow>
                  <TextField
                    label="PAN"
                    name="PAN"
                    value={companyData.PAN}
                    error={!!errors.PAN}
                    helperText={errors.PAN}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={6}>
                <Tooltip title="Enter TAN Number(eg: ABCD09878H)" placement="top" arrow>
                  <TextField
                    label="TAN"
                    name="tan"
                    value={companyData.tan}
                    error={!!errors.tan}
                    helperText={errors.tan}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Tooltip>
              </Grid>
              
              <Grid item xs={6}>
                <Tooltip title="Enter State Goods and Services Tax rate or code applicable to the company" placement="top" arrow>
                  <TextField
                    label="SGST"
                    name="SGST"
                    value={companyData.SGST}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.SGST}
                    helperText={errors.SGST}
                    required
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={6}>
                <Tooltip title="Enter Central Goods and Services Tax rate or code applicable to the company" placement="top" arrow>
                  <TextField
                    label="CGST"
                    name="CGST"
                    value={companyData.CGST}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.CGST}
                    helperText={errors.CGST}
                    required
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={6}>
                <Tooltip title="Enter Employee's State Insurance registration number" placement="top" arrow>
                  <TextField
                    label="ESI"
                    name="ESI"
                    value={companyData.ESI}
                    onChange={handleChange}
                    fullWidth
                  />
                </Tooltip>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {(!companyData.country || companyData.country.toLowerCase() === "india") && (
        <Paper elevation={3} sx={{ padding: 3, marginBottom: 3 }}>
          <Typography variant="h6" gutterBottom>
            PF/Trust Details (if applicable)
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Tooltip title="Enter Trust Name" placement="top" arrow>
                <TextField
                  label="Trust Name"
                  name="pfTrustName"
                  value={companyData.pfTrustName || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Tooltip>
            </Grid>

            <Grid item xs={6}>
              <Tooltip title="Enter Registration Number of the Trust" placement="top" arrow>
                <TextField
                  label="Registration Number"
                  name="pfRegno"
                  value={companyData.pfRegno || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Tooltip title="Enter Trust Address" placement="top" arrow>
                <TextField
                  label="Trust Address"
                  name="pfAddress"
                  value={companyData.pfAddress || ""}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, marginTop: 3 }}>
        <Button 
          variant="contained" 
          onClick={handleSubmit} 
          size="large"
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} /> : (isEditing ? "Update" : "Submit")}
        </Button>
        
  
      </Box>
    </Box>
  );
};

export default CompanyDetails;